import {createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'
import {eq} from '@shared/helpers/lang'
import {reduceValueKeys} from '@shared/helpers/object'

import {OrderSheetPageSet, OrderSheetPageSetType} from '@web/core/constants'
import {CommonState} from '@web/slices/common/slice'
import {deliveryGroupSelector, DeliveryGroupState} from '@web/slices/deliveryGroup/slice'
import {addressesInfoSelector, DeliveryInfoState} from '@web/slices/deliveryInfo/slice'
import {
    OrderSheetCouponState,
    prevTmpSelectedCouponKeyInfoSelector,
    selectedCouponKeyInfoSelector,
} from '@web/slices/orderSheetCoupon/slice'
import {orderSheetProductInfoSelector, OrderSheetProductsState} from '@web/slices/orderSheetProducts/slice'

import api from './apis'
import {CouponError, CouponsReloadResponse, ProductDiscountResponse} from './interfaces'

/** 선택하려고하는 상품 할인 정보 조회 */
export const getSelectProductDiscount = createAsyncThunk<
    {
        data: ProductDiscountResponse
        orderSheetItemIdByMerchantNo: {
            [merchantNo: number]: string[]
        }
    },
    {
        pageSet: Extract<OrderSheetPageSetType, 'MALL_CHECKOUT' | 'EASY_BOOK_CHECKOUT' | 'SELLER_CHECKOUT'>
        isCouponFailed: boolean
    },
    {
        state: {
            common: CommonState
            orderSheetProducts: OrderSheetProductsState
            orderSheetCoupon: OrderSheetCouponState
            deliveryInfo: DeliveryInfoState
        }
        rejectValue: CouponError
    }
>('coupon/getProductDiscount', async ({pageSet, isCouponFailed}, {getState, rejectWithValue}) => {
    const state = getState()
    try {
        const {
            product: productCouponIdsByOrderSheetItemId,
            duplicate: selectedProductDuplicateCouponIdByOrderSheetItemId,
            immediate: selectedImmediateDiscountPolicyNosByOrderSheetItemId,
            store: selectedStoreCouponIdsByMerchantNo,
            delivery: selectedDeliveryFeeCouponIdsByDeliveryGroupKey,
            multiPurchase: multiPurchaseDiscountSelectedOrderSheetItemIds,
        } = prevTmpSelectedCouponKeyInfoSelector(state)
        const {deliveryAddress} = addressesInfoSelector(state)

        /**
         * C1의 경우 할인금액 재조회 시 중복할인쿠폰을 상품쿠폰에 녹여서 호출해야함
         */
        const selectedProductCouponIdsByOrderSheetItemId = reduceValueKeys<
            Record<string, string[]>,
            Record<string, string>
        >(
            (acc, _, key) => {
                if (!eq(pageSet, OrderSheetPageSet.SELLER) && selectedProductDuplicateCouponIdByOrderSheetItemId[key]) {
                    acc[key] = acc[key]
                        ? [...acc[key], selectedProductDuplicateCouponIdByOrderSheetItemId[key]]
                        : [selectedProductDuplicateCouponIdByOrderSheetItemId[key]]
                }
                return acc
            },
            productCouponIdsByOrderSheetItemId,
            selectedProductDuplicateCouponIdByOrderSheetItemId,
        )

        const result = await api.getProductCouponDiscount(pageSet, {
            orderSheetId: state.common.seqID,
            discountRequest: {
                selectedProductCouponIdsByOrderSheetItemId: isCouponFailed
                    ? {}
                    : selectedProductCouponIdsByOrderSheetItemId,
                ...(eq(pageSet, OrderSheetPageSet.SELLER)
                    ? {
                          selectedProductDuplicateCouponIdByOrderSheetItemId: isCouponFailed
                              ? {}
                              : selectedProductDuplicateCouponIdByOrderSheetItemId,
                          selectedImmediateDiscountPolicyNosByOrderSheetItemId,
                          selectedStoreCouponIdsByMerchantNo: isCouponFailed ? {} : selectedStoreCouponIdsByMerchantNo,
                          selectedDeliveryFeeCouponIdsByDeliveryGroupKey: isCouponFailed
                              ? {}
                              : selectedDeliveryFeeCouponIdsByDeliveryGroupKey,
                          zipCode: deliveryAddress.zipCode,
                          buildingManagementNo: deliveryAddress.buildingManagementNo,
                          multiPurchaseDiscountSelectedOrderSheetItemIds,
                      }
                    : {}),
            },
        })

        const {orderSheetItemIdByMerchantNo} = orderSheetProductInfoSelector(state)

        return {
            data: result,
            orderSheetItemIdByMerchantNo,
        }
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})

/**
 * 할인 정보 재조회 (주문서 복원 case)
 **/
export const updateCouponDiscount = createAsyncThunk<
    ProductDiscountResponse,
    {pageSet: Extract<OrderSheetPageSetType, 'MALL_CHECKOUT' | 'EASY_BOOK_CHECKOUT' | 'SELLER_CHECKOUT'>},
    {
        state: {
            common: CommonState
            orderSheetProducts: OrderSheetProductsState
            orderSheetCoupon: OrderSheetCouponState
            deliveryInfo: DeliveryInfoState
        }
        rejectValue: CouponError
    }
>('coupon/updateCouponDiscount', async ({pageSet}, {getState, rejectWithValue}) => {
    const state = getState()

    const {
        product: productCouponIdsByOrderSheetItemId,
        duplicate: selectedProductDuplicateCouponIdByOrderSheetItemId,
        immediate: selectedImmediateDiscountPolicyNosByOrderSheetItemId,
        store: selectedStoreCouponIdsByMerchantNo,
        delivery: selectedDeliveryFeeCouponIdsByDeliveryGroupKey,
    } = selectedCouponKeyInfoSelector(state)
    const {deliveryAddress} = addressesInfoSelector(state)

    try {
        /**
         * C1의 경우 할인금액 재조회 시 중복할인쿠폰을 상품쿠폰에 녹여서 호출해야함
         */
        const selectedProductCouponIdsByOrderSheetItemId = reduceValueKeys<
            Record<string, string[]>,
            Record<string, string>
        >(
            (acc, _, key) => {
                if (!eq(pageSet, OrderSheetPageSet.SELLER) && selectedProductDuplicateCouponIdByOrderSheetItemId[key]) {
                    acc[key] = acc[key]
                        ? [...acc[key], selectedProductDuplicateCouponIdByOrderSheetItemId[key]]
                        : [selectedProductDuplicateCouponIdByOrderSheetItemId[key]]
                }
                return acc
            },
            productCouponIdsByOrderSheetItemId,
            selectedProductDuplicateCouponIdByOrderSheetItemId,
        )

        const result = await api.getProductCouponDiscount(pageSet, {
            orderSheetId: state.common.seqID,
            discountRequest: {
                selectedProductCouponIdsByOrderSheetItemId,
                ...(eq(pageSet, OrderSheetPageSet.SELLER)
                    ? {
                          selectedProductDuplicateCouponIdByOrderSheetItemId,
                          selectedImmediateDiscountPolicyNosByOrderSheetItemId,
                          selectedStoreCouponIdsByMerchantNo,
                          selectedDeliveryFeeCouponIdsByDeliveryGroupKey,
                          zipCode: deliveryAddress.zipCode,
                          buildingManagementNo: deliveryAddress.buildingManagementNo,
                      }
                    : {}),
            },
        })
        return result
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})

/**
 * 아래 2가지 case에서 사용하는 액션
 * 1. 쿠폰페이지 내 > 최대적용 혜택계산기 쿠폰적용 조회
 * 2. 배송비 쿠폰 재조회
 *
 * 배송비 쿠폰 재조회
 * 1. 복원 시
 * 2. 배송지 변경 시
 *
 * 배송지 변경시에만 배송비 최적혜택 적용이 필요
 * 복원 시에는 기존 배송비 쿠폰 선택을 그대로 반영한다. (shouldOptimize 필드로 구분)
 * */
export const getCoupons = createAsyncThunk<
    {
        coupons: CouponsReloadResponse
        deliveryGroupsByKey: DeliveryGroupState['deliveryGroupsByKey']
        directSelect: boolean
    },
    {
        pageSet: Extract<OrderSheetPageSetType, 'MALL_CHECKOUT' | 'EASY_BOOK_CHECKOUT' | 'SELLER_CHECKOUT'>
        /** 주문서바닥페이지에서 쿠폰재조회할 때 적용하는 flag */
        directSelect?: boolean
    },
    {
        state: {
            common: CommonState
            deliveryInfo: DeliveryInfoState
            deliveryGroup: DeliveryGroupState
        }
        rejectValue: CouponError
    }
>('coupon/getCoupons', async ({pageSet, directSelect = false}, {getState, rejectWithValue}) => {
    const state = getState()
    const {deliveryAddress} = addressesInfoSelector(state)
    try {
        const result = await api.getCoupons({
            pageSet,
            orderSheetId: state.common.seqID,
            zipCode: deliveryAddress.zipCode,
            buildingManagementNo: deliveryAddress.buildingManagementNo,
            ...(pageSet === OrderSheetPageSet.SELLER
                ? {}
                : {
                      address: deliveryAddress.baseAddress,
                  }),
        })

        return {
            coupons: result,
            deliveryGroupsByKey: deliveryGroupSelector(state).deliveryGroupsByKey,
            directSelect,
        }
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})

/**
 * 배송비 쿠폰 재조회
 * 1. 복원 시
 * 2. 배송지 변경 시
 *
 * 배송비 변경시에만 배송비 최적혜택 적용
 */
export const updateDeliveryCoupons = createAsyncThunk<
    {
        deliveryCoupons: CouponsReloadResponse['deliveryFeeCoupons']
        deliveryGroupsByKey: DeliveryGroupState['deliveryGroupsByKey']
        shouldDeliveryCouponOptimize: boolean
    },
    {
        pageSet: Extract<OrderSheetPageSetType, 'MALL_CHECKOUT' | 'EASY_BOOK_CHECKOUT' | 'SELLER_CHECKOUT'>
        shouldDeliveryCouponOptimize: boolean
    },
    {
        state: {
            common: CommonState
            deliveryInfo: DeliveryInfoState
            deliveryGroup: DeliveryGroupState
        }
        rejectValue: CouponError
    }
>('coupon/getDeliveryCoupons', async ({pageSet, shouldDeliveryCouponOptimize}, {getState, rejectWithValue}) => {
    const state = getState()
    const {deliveryAddress} = addressesInfoSelector(state)
    try {
        const result = await api.getCoupons({
            pageSet,
            orderSheetId: state.common.seqID,
            zipCode: deliveryAddress.zipCode,
            ...(pageSet === OrderSheetPageSet.SELLER
                ? {}
                : {
                      address: deliveryAddress.baseAddress,
                  }),
            ...(deliveryAddress.buildingManagementNo
                ? {buildingManagementNo: deliveryAddress.buildingManagementNo}
                : {}),
        })

        return {
            deliveryCoupons: result.deliveryFeeCoupons,
            deliveryGroupsByKey: deliveryGroupSelector(state).deliveryGroupsByKey,
            shouldDeliveryCouponOptimize,
        }
    } catch (e) {
        if (isApiError(e)) {
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})
